import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const numericKeys = ["margin_top","margin_bottom","margin_left","margin_right","body_font_size","line_height","chapter_title_size","chapter_label_size","subheader_size","pullquote_size","bullet_spacing"];
const humanize = (s) => s.replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase());

export default function TabLayout({ project, meta, onChange }) {
  const [l, setL] = useState(project.layout);
  const [template, setTemplate] = useState(project.template || "book_premium");
  const [applyTheme, setApplyTheme] = useState(false);
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setL(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ph360/settings/layout", { template, layout: l, apply_theme_pack: applyTheme });
      onChange(data);
      setL(data.layout);
      toast.success("Layout saved");
      setApplyTheme(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const T = (k) => <input type="text" value={l[k] ?? ""} onChange={(e)=>upd(k,e.target.value)} data-testid={`layout-${k}`}/>;
  const S = (k, opts) => (
    <select value={l[k] ?? ""} onChange={(e)=>upd(k,e.target.value)} data-testid={`layout-${k}`}>
      {Object.entries(opts).map(([v,label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  );
  const C = (k, label) => (
    <label className="checkbox-line" key={k}>
      <input type="checkbox" checked={!!l[k]} onChange={(e)=>upd(k,e.target.checked)} data-testid={`layout-${k}`}/>
      {label}
    </label>
  );
  return (
    <>
      <div className="notice notice-warning ef360-lock-notice">
        <p><strong>PH360 House Style:</strong> Turn this on to force the official PublishingHub360 layout during export. Manual layout fields remain visible for reference, but locked exports use the PH360 house defaults.</p>
      </div>
      <h2>PublishingHub360 Style Lock</h2>
      <label className="checkbox-line">
        <input type="checkbox" checked={!!l.ph360_style_locked} onChange={(e)=>upd("ph360_style_locked",e.target.checked)} data-testid="layout-ph360_style_locked"/>
        🔒 Lock PublishingHub360 Style (Recommended for production exports)
      </label>
      <p className="description">Locked style uses stacked centered chapter headings, 90% chapter width, accent divider, PH360 badge, page numbers, publisher footer, and consistent PH360 spacing.</p>
      <hr/>
      <h2>Theme Pack Preset</h2>
      <p className="description">Choose a one-click PH360 style preset. Select Custom / Manual Settings if you want to keep individual layout controls.</p>
      <label>PH360 Theme Pack</label>{S("theme_pack", meta.theme_packs)}
      <label className="checkbox-line" style={{marginTop:8}}>
        <input type="checkbox" checked={applyTheme} onChange={(e)=>setApplyTheme(e.target.checked)} data-testid="layout-apply_theme_pack"/>
        Apply selected theme pack now
      </label>
      <hr/>
      <label>Template</label>
      <select value={template} onChange={(e)=>setTemplate(e.target.value)} data-testid="layout-template">
        {Object.entries(meta.templates).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {numericKeys.map(k => <React.Fragment key={k}><label>{humanize(k)}</label>{T(k)}</React.Fragment>)}
      {C("show_header_title","Show Book Title in Header")}
      {C("show_page_numbers","Show Page Numbers")}
      {C("show_publisher_footer","Show Publisher Footer")}
      {C("chapter_cover_pages","Chapter Cover Pages")}
      {C("image_placeholders","Image Placeholders")}
      <hr/><h3>Title Page Cover Image</h3>
      {C("title_page_cover_enabled","Show eBook Cover on Title Page")}
      <label>Cover Image URL</label>{T("title_page_cover_url")}
      <p className="description">Paste an image URL. The cover appears below the subtitle and above the author/publisher line.</p>
      <label>Cover Width (inches)</label>{T("title_page_cover_width")}
      <label>Cover Max Height (inches)</label>{T("title_page_cover_max_height")}
      <label>Drop Caps</label>{S("dropcaps", meta.dropcaps)}
      <h3>Chapter Design System</h3>
      <p className="description">Manual controls below can override the selected theme. To preserve manual tweaks, use Custom / Manual Settings.</p>
      <label>Chapter Label Style</label>{S("chapter_label_style", meta.chapter_label_styles)}
      <label>Chapter Accent / Topic Color</label>{S("chapter_accent", meta.chapter_accents)}
      <label>Custom Accent Hex Color</label>{T("chapter_custom_accent")}
      <label>Chapter Top Spacing</label>{T("chapter_top_spacing")}
      <label>Chapter Bottom Spacing</label>{T("chapter_bottom_spacing")}
      <label>Chapter Badge Gap Below</label>{T("chapter_badge_gap")}
      <label>Chapter Badge Right Gap</label>{T("chapter_badge_right_gap")}
      <label>Chapter Max Width Percent</label>{T("chapter_max_width")}
      <label>Chapter Alignment</label>{S("chapter_title_align", {left:"Left",center:"Center",right:"Right"})}
      <label>Chapter Line Style</label>{S("chapter_line_style", meta.chapter_line_styles)}
      <label>Chapter Line Width</label>{T("chapter_line_width")}
      <label>Chapter Title Layout</label>{S("chapter_title_layout", meta.chapter_title_layouts)}
      {C("premium_opening_paragraph","Premium Opening Paragraph")}
      <label>Chapter Cover Pages</label>{S("chapter_cover_frequency", meta.chapter_cover_frequencies)}
      <label>Chapter Cover Style</label>{S("chapter_cover_style", meta.chapter_cover_styles)}
      <p style={{marginTop:14}}>
        <button className="button button-primary" onClick={save} disabled={busy} data-testid="layout-save">
          {busy ? "Saving…" : "Save Layout"}
        </button>
      </p>
    </>
  );
}
